/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import styled, { css } from 'styled-components';
import { stylePropertiesToCss } from '@anwb/style-mixins';
import { STATIC_BASE_URL, pxToRem } from '@anwb/style-utilities';
import Typography from '@anwb/typography';
const BUTTON_HEIGHT_SMALL = 40;
const BUTTON_HEIGHT_NORMAL_MOBILE = 48;
const BUTTON_HEIGHT_NORMAL_DESKTOP = 56;
const ICON_SIZE = 48;
/** Style overwrites for core-ui */
export const overrideCoreUiStyles = ({ theme }) => css `
	background: initial;
	color: unset;
	border: none;
	padding: initial;
	text-decoration: none;
	text-transform: none;
	cursor: pointer;
	outline: none;

	&:hover,
	&:active,
	&:focus {
		border-color: unset;
		color: unset;
	}

	&[disabled] {
		opacity: initial;
	}

	&[type='submit'] {
		${stylePropertiesToCss(Object.assign({}, theme.typography.buttons['100ButtonLink']))}
		background-image: none;
		&:hover,
		&:active,
		&:focus {
			background-image: none;
		}
	}
`;
/** Styles for the disabled state of the button. This is the same for all color buttons */
export const disabledButtonStyles = ({ theme, $variant, }) => css `
	background-color: ${theme.colors.buttons.bgButtonDisabledOnLight};
	color: ${theme.colors.buttons.textButtonDisabled};

	${$variant === 'on-medium' &&
    css `
		background-color: ${theme.colors.buttons.bgButtonDisabledOnMedium};
		color: ${theme.colors.buttons.textButtonDisabledOnMedium};
	`}

	${$variant === 'on-dark' &&
    css `
		background-color: ${theme.colors.buttons.bgButtonDisabledOnDark};
		color: ${theme.colors.buttons.textButtonDisabledOnDark};
	`}
`;
/**
 * ButtonPrimary
 */
export const buttonPrimaryStyles = ({ theme, $variant, }) => css `
	background-color: ${theme.colors.buttons.bgButtonPrimary};
	color: ${theme.colors.buttons.textButtonPrimary};

	&:hover {
		background-color: ${theme.colors.buttons.bgButtonPrimaryHover};
	}

	&:active {
		background-color: ${theme.colors.buttons.bgButtonPrimaryActive};
	}

	&:focus-visible {
		outline: ${theme.borderWidth.m}px solid ${theme.colors.highlight.borderFocus};
		outline-offset: ${theme.borderWidth.m}px;

		${$variant === 'on-dark' &&
    css `
			outline: ${theme.borderWidth.m}px solid ${theme.colors.highlight.borderFocusOnDark};
		`};
	}

	&[disabled] {
		${disabledButtonStyles}
	}
`;
/**
 * ButtonSecondary
 */
export const buttonSecondaryStyles = ({ theme, $variant, }) => css `
	background-color: ${theme.colors.buttons.bgButtonSecondary};
	color: ${theme.colors.buttons.textButtonSecondary};

	${$variant === 'on-dark' &&
    css `
		background-color: ${theme.colors.buttons.bgButtonSecondaryOnDark};
	`}

	${$variant === 'on-dark' &&
    css `
		color: ${theme.colors.buttons.textButtonSecondaryOnDark};
	`}

	&:hover {
		background-color: ${theme.colors.buttons.bgButtonSecondaryHover};
		color: ${theme.colors.buttons.textButtonSecondary};

		${$variant === 'on-dark' &&
    css `
			background-color: ${theme.colors.buttons.bgButtonSecondaryOnDarkHover};
			color: ${theme.colors.buttons.textButtonSecondaryOnDark};
		`}
	}

	&:active {
		background-color: ${theme.colors.buttons.bgButtonSecondaryActive};

		${$variant === 'on-dark' &&
    css `
			background-color: ${theme.colors.buttons.bgButtonSecondaryOnDarkActive};
		`}
	}

	&:focus {
		color: ${theme.colors.buttons.textButtonSecondary};

		${$variant === 'on-dark' &&
    css `
			color: ${theme.colors.buttons.textButtonSecondaryOnDark};
		`}
	}

	&:focus-visible {
		color: ${theme.colors.buttons.textButtonSecondary};
		outline: ${theme.borderWidth.m}px solid ${theme.colors.highlight.borderFocus};
		outline-offset: ${theme.borderWidth.m}px;

		${$variant === 'on-dark' &&
    css `
			outline: ${theme.borderWidth.m}px solid ${theme.colors.highlight.borderFocusOnDark};
			background-color: ${theme.colors.buttons.bgButtonSecondaryOnDark};
			color: ${theme.colors.buttons.textButtonSecondaryOnDark};
		`}
	}

	&[disabled] {
		${disabledButtonStyles}
	}
`;
/**
 * ButtonTertiary
 */
export const buttonTertiaryStyles = ({ theme, $variant, }) => css `
	background: transparent;
	border: ${theme.borderWidth['2']}px solid ${theme.colors.buttons.borderButtonTertiary};
	color: ${theme.colors.buttons.textButtonTertiary};

	${$variant === 'on-medium' &&
    css `
		border-color: ${theme.colors.buttons.borderButtonTertiaryOnMedium};
	`};

	${$variant === 'on-dark' &&
    css `
		border-color: ${theme.colors.buttons.borderButtonTertiaryOnDark};
		color: ${theme.colors.blanc.textButton};
	`}

	&:hover {
		background-color: ${theme.colors.buttons.bgButtonTertiaryHover};
		color: ${theme.colors.buttons.textButtonTertiary};
		border-color: ${theme.colors.buttons.borderButtonTertiary};

		${$variant === 'on-medium' &&
    css `
			background-color: ${theme.colors.buttons.bgButtonTertiaryOnMediumHover};
		`};

		${$variant === 'on-dark' &&
    css `
			background-color: ${theme.colors.buttons.bgButtonTertiaryOnDarkHover};
			color: ${theme.colors.blanc.textButton};
			border-color: ${theme.colors.buttons.borderButtonTertiaryOnDarkHover};
		`}
	}

	&:active {
		background-color: ${theme.colors.buttons.bgButtonTertiaryActive};
		color: ${theme.colors.buttons.textButtonTertiary};
		border: ${theme.borderWidth['2']}px solid ${theme.colors.buttons.borderButtonTertiary};

		${$variant === 'on-medium' &&
    css `
			background-color: ${theme.colors.buttons.bgButtonTertiaryOnMediumActive};
		`};

		${$variant === 'on-dark' &&
    css `
			background-color: ${theme.colors.buttons.bgButtonTertiaryOnDarkActive};
			color: ${theme.colors.blanc.textButton};
			border-color: ${theme.colors.buttons.borderButtonTertiaryOnDarkActive};
		`}
	}

	&:focus-visible {
		// Core UI

		&& {
			outline: ${theme.borderWidth.m}px solid ${theme.colors.highlight.borderFocus};
			outline-offset: ${theme.borderWidth.m}px;

			${$variant === 'on-dark' &&
    css `
				outline: ${theme.borderWidth.m}px solid ${theme.colors.highlight.borderFocusOnDark};
			`}
		}
	}

	&:focus {
		color: ${theme.colors.buttons.textButtonTertiary};

		${$variant === 'on-dark' &&
    css `
			color: ${theme.colors.blanc.textButton};
		`}
	}

	&[disabled] {
		${disabledButtonStyles}
	}
`;
/** Button size styles */
export const buttonSizeStyles = ({ theme, $size }) => css `
	width: 100%;

	@media (min-width: ${pxToRem(theme.viewportBreakpoint.sm)}) {
		width: auto;
	}

	${$size === 'normal' &&
    css `
		padding: 0 ${pxToRem(theme.spacing['300'])};
		height: ${pxToRem(BUTTON_HEIGHT_NORMAL_MOBILE)};

		@media (min-width: ${pxToRem(theme.viewportBreakpoint.lg)}) {
			padding: 0 ${pxToRem(theme.spacing['400'])};
			height: ${pxToRem(BUTTON_HEIGHT_NORMAL_DESKTOP)};
		}
	`}

	${$size === 'small' &&
    css `
		padding: 0 ${pxToRem(theme.spacing['200'])};
		height: ${pxToRem(BUTTON_HEIGHT_SMALL)};
	`}
`;
/**
 * Button container
 */
export const buttonContainerStyles = ({ theme, $variant, $color, $size, }) => css `
	${overrideCoreUiStyles}
	${buttonSizeStyles({ theme, $size })}

	${$color === 'primary' && buttonPrimaryStyles({ theme, $variant })}
	${$color === 'secondary' && buttonSecondaryStyles({ theme, $variant })}
	${$color === 'tertiary' && buttonTertiaryStyles({ theme, $variant })}

	display: inline-flex;
	justify-content: center;
	align-items: center;
	gap: ${pxToRem(theme.spacing['100'])};
	border-radius: ${pxToRem(theme.borderRadius.s)};
`;
export const ButtonContainer = styled(Typography).attrs(({ $size }) => ({
    variant: $size === 'normal' ? 'button-link' : 'button-link-xs',
})) `
	${buttonContainerStyles};

	&[type='submit'] {
		${buttonContainerStyles}
	}
`;
/**
 * Button iconcontainer
 */
export const buttonIconContainerStyles = ({ theme, $variant, $color, }) => css `
	${overrideCoreUiStyles};

	width: ${pxToRem(ICON_SIZE)};
	height: ${pxToRem(ICON_SIZE)};
	border-radius: ${pxToRem(theme.borderRadius.s)};

	${$color === 'primary' && buttonPrimaryStyles({ theme, $variant })}
	${$color === 'secondary' && buttonSecondaryStyles({ theme, $variant })}
	${$color === 'tertiary' && buttonTertiaryStyles({ theme, $variant })}
`;
export const ButtonIconContainer = styled.button `
	${buttonIconContainerStyles}
`;
export const buttonToggleButtonStyles = ({ theme, $isActive, }) => css `
	${overrideCoreUiStyles};
	flex: 1;
	padding-top: ${pxToRem(Number(theme.spacing['200']) - Number(theme.borderWidth['2']))};
	padding-bottom: ${pxToRem(Number(theme.spacing['200']) - Number(theme.borderWidth['2']))};
	border-radius: 0;
	background: ${theme.colors.blanc.bgTabboxContent};
	color: ${theme.colors.base.textButton};

	&& {
		${$isActive &&
    css `
			color: ${theme.colors.blanc.textButton};
			background: ${theme.colors.base.bgTabTabboxActive};

			&:after {
				content: url('${STATIC_BASE_URL}/poncho/button/images/toggle-icon.svg');
				// override core-ui
				left: auto;
				margin-left: ${pxToRem(theme.spacing['200'])};
				padding-left: 0;
			}
		`}
	}
`;
export const ButtonToggleButton = styled(Typography).attrs({
    variant: 'button-link',
    tagName: 'button',
}) `
	${buttonToggleButtonStyles};
`;
/**
 * Button toggle
 */
export const buttonToggleContainerStyles = ({ theme }) => css `
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	overflow: hidden;
	border: ${theme.borderWidth['2']}px solid ${theme.colors.base.borderTabbox};
	border-radius: ${pxToRem(theme.borderRadius.m)};
`;
export const ButtonToggleContainer = styled.div `
	${buttonToggleContainerStyles};
`;
//# sourceMappingURL=button.styled.js.map